import { isSetEqual } from '@attain-apps/data-utils'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type * as workspace from '../../workspace'
import type { Collection } from '../collection'
import { GLOBAL_WORKSPACE_ID } from '../types'

/** The name of the global conversations workspace */
const GLOBAL_CONVERSATIONS_WORKSPACE_NAME = 'Global Conversations'

/**
 * Effect that groups conversations by workspaceId using the groupPlugin.
 * Creates a group for each unique workspaceId found in the current list of
 * conversations. Uses the workspace collection to map workspace IDs to their
 * actual names.
 *
 * NOTE: - Conversations without a workspaceId or whose workspace is not
 * accessible to the user are grouped under `GLOBAL_WORKSPACE_ID`.
 */
export const putWorkspaceGroups = store.effects.create(
  (
    feature: Collection['feature'],
    collection: Collection,
    workspaceCollection: workspace.Collection,
  ) =>
    rxjs
      .combineLatest([
        collection.plugins.currentListPlugin.feature.selectors.entities
          .asObservable,
        workspaceCollection.plugins.cachePlugin.feature.selectors.lookup
          .asObservable,
      ])
      .pipe(
        // Extract unique workspace IDs from conversations and build a name
        // lookup map
        rxjs.map(([conversations, workspaceLookup]) => {
          // Create a map from workspace ID to workspace name for quick lookups
          const workspaceNameLookup = new Map(
            Object.values(workspaceLookup)
              .filter(TypeChecker.isNotNullish.raw)
              .map(ws => [ws.id, ws.name]),
          )

          // Collect workspace IDs from conversations, keeping only those
          // accessible to the user (present in the lookup)
          const workspaceIds = new Set(
            conversations
              .map(conversation => conversation.workspaceId)
              .filter(TypeChecker.isNotNullish.raw)
              .filter(id => !!workspaceLookup[id]),
          )

          return { workspaceIds, workspaceNameLookup }
        }),
        // Only emit when workspace IDs or names actually change to avoid
        // unnecessary updates
        rxjs.distinctUntilChanged(
          (a, b) =>
            isSetEqual(a.workspaceIds, b.workspaceIds) &&
            isSetEqual(
              new Set(a.workspaceNameLookup.entries()),
              new Set(b.workspaceNameLookup.entries()),
            ),
        ),
        // Create groups: one global group plus one for each workspace
        rxjs.map(({ workspaceIds, workspaceNameLookup }) =>
          collection.plugins.groupPlugin.feature.actions.put
            .from('putWorkspaceGroups (effect)')
            .create({
              groups: [GLOBAL_WORKSPACE_ID, ...workspaceIds].map(
                workspaceId => ({
                  entitySelector: {
                    // Conversations belonging to an accessible workspace match
                    // their specific group; conversations without a workspace
                    // (or from an inaccessible one) fall under the global group
                    predicate: conversation => {
                      const belongsToAccessibleWorkspace =
                        conversation.workspaceId &&
                        workspaceIds.has(conversation.workspaceId)

                      if (belongsToAccessibleWorkspace) {
                        return conversation.workspaceId === workspaceId
                      }

                      return workspaceId === GLOBAL_WORKSPACE_ID
                    },
                  },
                  id: workspaceId,
                  name:
                    workspaceId === GLOBAL_WORKSPACE_ID
                      ? GLOBAL_CONVERSATIONS_WORKSPACE_NAME
                      : (workspaceNameLookup.get(workspaceId) ?? workspaceId),
                }),
              ),
            }),
        ),
      ),
)
